'use client';
import { useCallback, useState, useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { DeleteConfirmationDialog } from '@/app/[locale]/(main)/profile/edit/_components/DeleteConfirmationDialog';
import AvatarSection from '@/app/[locale]/(main)/profile/edit/_components/ProfileInformation/AvatarSection';
import FormActions from '@/app/[locale]/(main)/profile/edit/_components/ProfileInformation/FormActions';
import ProfileForm from '@/app/[locale]/(main)/profile/edit/_components/ProfileInformation/ProfileForm';
import { I_Avatar, I_AvatarTabs } from '@/data/editProfileMock';
import { useRouter } from '@/i18n/navigation';
import {
  useGlobalData,
  useUpdateGlobalData
} from '@/providers/GlobalDataProvider';
import {
  profileFormSchema,
  T_ProfileFormData,
  createProfileFormDefaultValues
} from '@/schemas/profileForm.schema';
import { deleteUserAction } from '@/server/actions/user/deleteUser.action';
import { updateUserAction } from '@/server/actions/user/updateUser.action';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_ProfileForm } from '@/ts/enums/E_ProfileForm';
import { E_Routes } from '@/ts/enums/E_Routes';
import { T_Country } from '@/ts/types/T_Country';

interface I_ProfileInformationProps {
  countries: T_Country[];
  avatars: I_AvatarTabs | null;
}

function ProfileInformation({ countries, avatars }: I_ProfileInformationProps) {
  const t = useTranslations('editProfile');
  const router = useRouter();
  const user = useGlobalData('user');
  const updateGlobalData = useUpdateGlobalData();

  // State management
  const [isOpenChooseAvatar, setIsOpenChooseAvatar] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [originalLanguage, setOriginalLanguage] = useState(
    user?.language || 'en'
  );
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState<I_Avatar | null>(null);

  // Form setup
  const form = useForm<T_ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: useMemo(
      () => createProfileFormDefaultValues(user, countries),
      [countries, user]
    ),
    mode: 'onChange'
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty }
  } = form;
  const hasChanges = isDirty;

  useEffect(() => {
    if (user) {
      const formData = createProfileFormDefaultValues(user, countries);

      reset(formData);
      setOriginalLanguage(user.language);
    }
    setIsUserLoading(false);
  }, [user, countries, reset]);

  const avatarUrl = useMemo(() => {
    if (isUserLoading) return null;
    // Use selected avatar if available (optimistic update), otherwise use user's avatar
    const avatar = selectedAvatar || user?.avatar;
    const url = avatar?.url || '/assets/images/avatar.png';
    // Add cache-busting parameter using avatar UUID to force image reload when avatar changes
    if (avatar?.url && avatar?.uuid) {
      return `${url}?v=${avatar.uuid}`;
    }
    return url;
  }, [user, selectedAvatar, isUserLoading]);

  const toggleOpenChooseAvatar = useCallback(() => {
    setIsOpenChooseAvatar(prev => !prev);
  }, []);

  const handleCloseChooseAvatar = useCallback(() => {
    setIsOpenChooseAvatar(false);
  }, []);

  const handleSaveChanges = useCallback(
    async (data: T_ProfileFormData) => {
      if (!user) return;

      setIsSaving(true);
      try {
        const updateData = {
          nickname: data[E_ProfileForm.NICKNAME],
          hideActivity: data[E_ProfileForm.HIDE_ACTIVITY],
          language: data[E_ProfileForm.LANGUAGE],
          country: data[E_ProfileForm.COUNTRY]
        };

        const response = await updateUserAction(updateData);

        if (response?.status === 200 || response?.status === 201) {
          // Update global user data with the new values
          if (user) {
            updateGlobalData({
              user: {
                ...user,
                nickname: data[E_ProfileForm.NICKNAME],
                language: data[E_ProfileForm.LANGUAGE] as string,
                country: data[E_ProfileForm.COUNTRY] as string,
                hideActivity: data[E_ProfileForm.HIDE_ACTIVITY]
              }
            });
          }

          // Reset form with new data to clear isDirty state and update dropdown values
          const newFormData = createProfileFormDefaultValues(
            { ...user, ...data },
            countries
          );
          reset(newFormData);

          // Update original language for future comparisons
          setOriginalLanguage(data[E_ProfileForm.LANGUAGE]);

          toast.success(t('messages.profileUpdated'));

          if (data[E_ProfileForm.LANGUAGE] !== originalLanguage) {
          }
        } else {
          throw new Error(`Update failed with status: ${response?.status}`);
        }
      } catch {
        toast.error(t('messages.profileUpdateFailed'));
      } finally {
        setIsSaving(false);
      }
    },
    [user, countries, reset, t, updateGlobalData, originalLanguage]
  );

  const handleAvatarUpdate = useCallback(
    async (avatar: I_Avatar) => {
      // Optimistically update the UI with the selected avatar
      setSelectedAvatar(avatar);
      setIsUpdatingAvatar(true);

      // Optimistically update global data
      if (user) {
        updateGlobalData({
          user: {
            ...user,
            avatar: {
              uuid: avatar.uuid,
              url: avatar.url,
              name: avatar.name,
              size: avatar.size,
              type: avatar.type,
              createdAt: avatar.createdAt,
              updatedAt: avatar.updatedAt,
              deletedAt: null,
              alt: avatar.name || 'Avatar',
              uploadInProgress: false,
              uploadedByUUID: avatar.uploadedByUUID || null,
              folderUUID: avatar.folderUUID
            }
          }
        });
      }

      setIsOpenChooseAvatar(false);

      try {
        const response = await updateUserAction({ avatarUUID: avatar.uuid });

        if (response?.status === 200 || response?.status === 201) {
          if (response.data?.avatar && user) {
            // Update with the actual response data
            updateGlobalData({
              user: {
                ...user,
                avatar: response.data.avatar
              }
            });
            // Clear the optimistic state since we have the real data
            setSelectedAvatar(null);
          }
          toast.success(t('messages.avatarUpdated'));
        } else {
          throw new Error(
            `Avatar update failed with status: ${response?.status}`
          );
        }
      } catch (error) {
        console.error('Failed to update avatar:', error);
        // Revert optimistic update on error
        setSelectedAvatar(null);
        toast.error(t('messages.avatarUpdateFailed'));
      } finally {
        setIsUpdatingAvatar(false);
      }
    },
    [user, t, updateGlobalData]
  );

  const handleDeleteClick = useCallback(() => {
    setIsOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteUserAction();
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setIsDeleting(false);
      setIsOpenDeleteDialog(false);
    }
  }, []);

  return (
    <div className="grid">
      <div className="grid max-w-95 w-full mx-auto max-sm:pb-6 pb-24 gap-7">
        <AvatarSection
          avatarUrl={avatarUrl}
          isUserLoading={isUserLoading}
          isUpdatingAvatar={isUpdatingAvatar}
          avatars={avatars}
          onAvatarUpdate={handleAvatarUpdate}
          onToggleChooseAvatar={toggleOpenChooseAvatar}
          isOpenChooseAvatar={isOpenChooseAvatar}
          onCloseChooseAvatar={handleCloseChooseAvatar}
        />

        <ProfileForm form={form} countries={countries} />

        <FormActions
          isSaving={isSaving}
          hasChanges={hasChanges}
          onSaveChanges={handleSubmit(handleSaveChanges)}
          onDeleteClick={handleDeleteClick}
        />

        <DeleteConfirmationDialog
          open={isOpenDeleteDialog}
          onOpenChange={setIsOpenDeleteDialog}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      </div>
    </div>
  );
}

export default ProfileInformation;
